import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Phone,
  Mail,
  LocationOn as MapPin,
  AccessTime as Clock,
  Message as MessageSquare,
  ExpandMore,
  HelpOutline as Help,
  WhatsApp,
  Support as SupportIcon,
} from '@mui/icons-material';

const Support = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const contactMethods = [
    {
      icon: <Phone sx={{ fontSize: 40 }} />,
      title: 'Phone Support',
      value: '+91 7082828181',
      description: 'Available 24/7 for urgent issues',
      color: '#FF7700',
      action: 'tel:+917082828181',
    },
    {
      icon: <Mail sx={{ fontSize: 40 }} />,
      title: 'Email Support',
      value: 'support@tabtrips.com',
      description: 'Response within 2-4 hours',
      color: '#2196F3',
      action: 'mailto:support@tabtrips.com',
    },
    {
      icon: <WhatsApp sx={{ fontSize: 40 }} />,
      title: 'WhatsApp',
      value: '+91 7082828181',
      description: 'Quick responses via WhatsApp',
      color: '#25D366',
      action: 'https://wa.me/917082828181',
    },
    {
      icon: <Clock sx={{ fontSize: 40 }} />,
      title: 'Support Hours',
      value: '24/7 Available',
      description: 'Round the clock support',
      color: '#4CAF50',
    },
  ];

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer:
        'Click on "Forgot your password?" on the login page. Enter your email address and follow the instructions sent to your email. If you don\'t receive the email, check your spam folder or contact support.',
    },
    {
      question: 'How do I update my property information?',
      answer:
        'Log into your extranet portal, navigate to the "Property Management" section, and select the property you want to update. You can modify rates, availability, amenities, photos, and property details.',
    },
    {
      question: 'When will I receive my commission payments?',
      answer:
        'Commission payments are processed on a monthly basis, typically within 30 days of the booking confirmation. You can track your earnings and settlements in the Financial Reports and Settlements sections.',
    },
    {
      question: 'How do I add a new property to my account?',
      answer:
        'Contact our onboarding team at onboarding@tabtrips.com or call our support line at +91 7082828181. We\'ll guide you through the property registration process, which includes verification and setup.',
    },
    {
      question: 'What should I do if a guest has a complaint?',
      answer:
        'Immediately contact our customer service team at support@tabtrips.com or call +91 7082828181. We\'ll work with you to resolve the issue and ensure guest satisfaction. Document all communication for reference.',
    },
    {
      question: 'How can I optimize my property\'s visibility?',
      answer:
        'Ensure your property information is complete and accurate, maintain competitive pricing, respond quickly to inquiries, maintain high guest ratings, and use our promotional tools. Our team can provide personalized optimization strategies.',
    },
    {
      question: 'How do I manage room rates and inventory?',
      answer:
        'Use the Room Rates Calendar feature to set rates and inventory for specific dates. You can also use bulk update tools to set rates for multiple dates at once. Changes sync in real-time with our booking system.',
    },
    {
      question: 'Can I have multiple users for my account?',
      answer:
        'Yes! You can create multiple sub-accounts with different permission levels. Navigate to Settings > User Management to add team members and assign roles such as admin, manager, or staff.',
    },
    {
      question: 'How secure is my data?',
      answer:
        'We use industry-standard SSL encryption and secure payment gateways to protect all your data. Your information is stored securely and never shared with third parties without your consent.',
    },
    {
      question: 'How do I view my bookings and reservations?',
      answer:
        'Go to the Reservations page to see all your bookings. You can filter by date, status, or search by guest name or booking code. Each booking shows complete guest and payment details.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f9f9f9' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #FF7700 0%, #ff8800 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          overflow: 'hidden',
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '400px',
            height: '400px',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            filter: 'blur(80px)',
            transform: 'translate(30%, -30%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '300px',
            height: '300px',
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            transform: 'translate(-30%, 30%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  mb: 3,
                }}
              >
                <SupportIcon sx={{ fontSize: 48, color: 'white' }} />
              </Box>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              Support Center
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', md: '1.5rem' },
                maxWidth: '700px',
                mx: 'auto',
                opacity: 0.95,
                lineHeight: 1.6,
              }}
            >
              We're here to help you succeed with TabTrips Extranet. Our support team is available
              24/7 to assist you with any questions or concerns.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
            {/* Contact Information */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              Get in Touch
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Choose your preferred method to reach our support team
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {contactMethods.map((method, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 3,
                      borderRadius: '20px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      cursor: method.action ? 'pointer' : 'default',
                      '&:hover': method.action
                        ? {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                          }
                        : {},
                    }}
                    onClick={() => method.action && window.open(method.action)}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          p: 2,
                          borderRadius: '12px',
                          bgcolor: `${method.color}15`,
                          color: method.color,
                          mb: 2,
                        }}
                      >
                        {method.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {method.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: method.color, mb: 1 }}
                      >
                        {method.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

       

            {/* FAQ Section */}
        <Box>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: '12px',
                bgcolor: '#FF770015',
                color: '#FF7700',
                mb: 2,
              }}
            >
              <Help sx={{ fontSize: 40 }} />
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 2,
                color: '#2E4057',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Find answers to common questions about TabTrips Extranet
            </Typography>
          </Box>

          <Box>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === `panel-${index}`}
                onChange={handleChange(`panel-${index}`)}
                sx={{
                  mb: 1,
                  borderRadius: '12px !important',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: '0 0 8px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#FF7700' }} />}
                  sx={{
                    px: 3,
                    py: 2,
                    '&.Mui-expanded': {
                      bgcolor: '#FF770010',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, py: 2 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

       
      </Container>
    </Box>
  );
};

export default Support; 
